import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SegmentIcon from '@mui/icons-material/Segment';
import { Box, Button, Divider } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { assertNotNull } from '@remote-voice/utilities';
import Enumerable from 'linq';
import Queue from 'promise-queue';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { AdminContent } from '../../../components/atoms/AdminPageParts';
import { useCheckPermission } from '../../../components/hooks/useCheckPermission';
import useConfirmDeleteDialog from '../../../components/organisms/confirmDialog/useConfirmDeleteDialog';
import {
  TemplateMessage,
  useEditTemplateMessageMutation,
  useUpdateTemplateMessagesOrderMutation,
  useRemoveTemplateMessagesMutation,
  useTemplateMessageCategoriesQuery,
  useTemplateMessagesQuery,
  useTemplateMessagesLazyQuery,
} from '../../../types/graphql';

import AddButton from '@/components/atoms/AddButton';
import DeleteButton from '@/components/atoms/DeleteButton';
import DraggableDataGrid from '@/components/atoms/DraggableDataGrid';
import { DragHandle } from '@/components/atoms/DragHandle';
import { LineClampTypography } from '@/components/atoms/LineClampTypography';
import LoadingBackdrop from '@/components/atoms/LoadingBackdrop';
import { useSetHeaderTitle } from '@/components/hooks/useHeaderTitle';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import { DeletionFooterMenu } from '@/components/molecules/admin/DeletionFooterMenu';
import { ResponsiveFormHeader } from '@/components/molecules/admin/ResponsiveFormHeader';
import { TemplateGroupFooterMenu } from '@/components/molecules/admin/TemplateGroupFooterMenu';
import BreadcrumbBar from '@/components/organisms/BreadcrumbBar';

const TemplateIndex = () => {
  useCheckPermission({ tenantCompanyAdmin: true });

  const { t, i18n } = useTranslation('admin');
  const { t: commonT } = useTranslation('common');
  useSetHeaderTitle(commonT('breadcrumb.templateAdmin'));
  const userLang = i18n.language;

  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const categoryId = params.categoryId;

  const categoriesResult = useTemplateMessageCategoriesQuery({
    variables: { input: { id: categoryId } },
  });
  const templateMessagesResult = useTemplateMessagesQuery({
    // 一覧は表示中の言語しか使わないため、その言語だけ取得して軽量化する
    // （万一 userLang が空なら language を渡さず全言語取得にフォールバック）
    variables: { input: { categoryId, language: userLang || undefined } },
  });
  // グループ保存時は全言語が必要になる（editTemplateMessage が翻訳を全置換するため）。
  // 上の一覧クエリは言語フィルタ済みなので、保存直前に language 未指定で取得し直す用。
  const [getFullTemplateMessages] = useTemplateMessagesLazyQuery();

  const [removeTemplateMessages, removeTemplateMessagesResult] =
    useRemoveTemplateMessagesMutation();
  const [updateTemplateMessagesOrder, updateTemplateMessagesOrderResult] =
    useUpdateTemplateMessagesOrderMutation();
  const [editTemplateMessage] = useEditTemplateMessageMutation();

  const [rows, setRows] = useState<TemplateMessage[]>([]);
  // クエリ完了後も DataGrid の描画（多数行・非仮想化）に時間がかかる。
  // useEffect（paint 後に実行）だと「行がありません」の空グリッドが一瞬 paint され、
  // その後の重い setRows 再描画までローディングが消えてしまう。
  // useLayoutEffect（paint 前に同期実行）にすることで、空グリッドを paint せずに
  // 行反映後の描画を完了させ、その間は直前のローディング表示を継続させる。
  useLayoutEffect(() => {
    setRows(templateMessagesResult.data?.templateMessages ?? []);
  }, [templateMessagesResult.data?.templateMessages]);

  const [isInDeleteState, setIsInDeleteState] = useState(false);
  const [isGroupConfigMode, setIsGroupConfigMode] = useState(false);
  // グループ保存の二重送信ガード（onSave が await するためメニューが押下可能なまま残る）
  const [isSavingGroup, setIsSavingGroup] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  const confirmDelete = useConfirmDeleteDialog();

  const [canBind, setCanBind] = useState(false);
  const [canUnbind, setCanUnbind] = useState(false);
  // 選択項目変更時に
  useEffect(() => {
    const selectedItems = selection.map((x) => {
      const item = rows.find((y) => y.id === x);
      assertNotNull(item);
      return item;
    });

    const groupCount = Enumerable.from(
      selectedItems.map((x) => x.groupId ?? x.id)
    )
      .distinct()
      .count();
    setCanBind(groupCount >= 2);

    const sameGroupCount = Enumerable.from(
      selectedItems.map((x) => x.groupId ?? x.id)
    )
      .groupBy((x) => x)
      .select((x) => x.count() - 1)
      .sum();
    setCanUnbind(sameGroupCount >= 1);
  }, [rows, selection]);

  const saveRows = useCallback(
    async (newRows: TemplateMessage[]) => {
      assertNotNull(categoryId);
      await updateTemplateMessagesOrder({
        variables: {
          input: {
            templateCategoryId: categoryId ?? '',
            items: newRows.map((x) => ({
              id: x.id,
              order: x.order,
            })),
          },
        },
      });
    },
    [categoryId, updateTemplateMessagesOrder]
  );

  const promiseQueue = new Queue(20);

  const saveTemplateGroupSetting = async (newRows: TemplateMessage[]) => {
    // editTemplateMessage は translation を全置換（deleteMany→createMany）するため、
    // 言語フィルタ済みの一覧データ(x.languages)をそのまま送ると他言語が消える。
    // グループ設定で変えたいのは order/groupId のみなので、全言語を取得し直して保持する。
    const fullResult = await getFullTemplateMessages({
      variables: { input: { categoryId } },
      fetchPolicy: 'no-cache',
    });
    const fullMessages = fullResult.data?.templateMessages;
    // 全言語データを取得できなければ保存を中断する（フェイルセーフ）。
    // ここでフィルタ済みデータにフォールバックすると editTemplateMessage が
    // 他言語を全削除してしまうため、部分的にも書き込まず明示的に失敗させる。
    if (fullResult.error || fullMessages == null) {
      throw (
        fullResult.error ?? new Error('failed to load full template languages')
      );
    }
    const fullById = new Map(fullMessages.map((m) => [m.id, m]));
    // ミューテーション開始前に全対象の全言語データが揃っているか検証する。
    // 揃わない場合はここで throw し、editTemplateMessage を1件も呼ばない
    // （部分的に書き込まれて他言語が欠落するのを防ぐ）。
    const targets = newRows.map((x) => {
      const source = fullById.get(x.id);
      if (source == null) {
        throw new Error(
          `full languages not found for template ${x.id}; aborting group save to avoid data loss`
        );
      }
      return { row: x, source };
    });
    await Promise.all(
      targets.map(({ row: x, source }) =>
        promiseQueue.add(() =>
          editTemplateMessage({
            variables: {
              input: {
                id: x.id,
                files: source.files.map((y) => ({
                  id: y.id,
                  fileName: y.fileName,
                  fileType: y.fileType,
                })),
                languages: source.languages.map((y) => ({
                  language: y.language,
                  message: y.message,
                  readonly: y.readonly,
                  voiceRate: y.voiceRate,
                })),
                templateCategoryId: x.category.id,
                order: x.order,
                groupId: x.groupId,
              },
            },
          })
        )
      )
    );
  };

  const onChange = useCallback(
    async (params: any[], _oldIndex: number, newIndex: number) => {
      // 並べ替え
      // グループ内外へのドラッグ時、グループが分断されないように調整。

      // idのリストからオブジェクトを検索してオブジェクトのリストに変換
      const changedRows = Enumerable.from(params).select((p) =>
        Enumerable.from(rows).first((r) => r.id === p.id)
      );
      const movedRow = changedRows.elementAt(newIndex);

      const newRows = changedRows
        // orderを振りなおして
        .select((row, i) => ({ ...row, order: i + 1 }))
        // グループでグループ化して
        .groupBy((row) => row.groupId ?? row.id)
        // 1つめのorderで並び替えて（並び替えたオブジェクトがグループにあれば、そのorderを優先する）
        .orderBy(
          (row) =>
            row.firstOrDefault((x) => x.id === movedRow.id)?.order ??
            row.first().order
        )
        // 配列をフラットにして
        .selectMany((row) => row)
        // orderを再度振りなおす
        .select((row, i) => ({ ...row, order: i + 1 }))
        .toArray();

      setRows(newRows);
      await saveRows(newRows);
    },
    [rows, saveRows]
  );

  const displayRows = useMemo(
    () =>
      rows
        .sort((a, b) => a.order - b.order)
        .map((message) => {
          const templateText = message.languages.find(
            (m) => m.language === userLang
          )?.message;
          return {
            category: message.category.name,
            id: message.id,
            templateText: templateText,
            date: new Date(message.createdAt).toLocaleDateString(),
            order: message.order,
          };
        }),
    [rows, userLang]
  );

  return (
    <Box>
      <LoadingBackdrop
        open={categoriesResult.loading || templateMessagesResult.loading}
      />
      <BreadcrumbBar keyFrom="/admin/template" />

      <AdminContent>
        <ResponsiveFormHeader
          title={
            categoriesResult.data?.templateMessageCategories[0].name ?? '　'
          }
          titleVarient="h4"
          toolbuttons={
            isGroupConfigMode
              ? []
              : [
                  ...(isInDeleteState
                    ? []
                    : [
                        <Button
                          key="configGroup"
                          startIcon={<SegmentIcon />}
                          onClick={() => {
                            // グループ化(連投設定)モードに移行する
                            setIsGroupConfigMode(true);
                          }}
                        >
                          グループ化
                        </Button>,
                      ]),
                  <AddButton
                    key="add"
                    onClick={() =>
                      navigate('/admin/template/add', { state: { categoryId } })
                    }
                  />,
                  ...(isInDeleteState === false
                    ? [
                        <DeleteButton
                          key="remove"
                          onClick={() => setIsInDeleteState(true)}
                        />,
                      ]
                    : []),
                ]
          }
        />
        <Divider sx={{ mt: 4 }} />
        <DraggableDataGrid
          key={`draggable-grid-${isGroupConfigMode ? 'group' : 'normal'}`}
          autoHeight
          columnHeaderHeight={40}
          columns={[
            {
              field: 'templateText',
              headerName: t('template.template'),
              minWidth: 150,
              flex: 1,
              sortable: false,
              renderCell: (params) => (
                <LineClampTypography variant="body1" rowCount={3} my={1.5}>
                  {params.value}
                </LineClampTypography>
              ),
            },
            {
              field: 'group',
              headerName: '連投',
              width: 20,
              sortable: false,
              renderCell: (params) => {
                const item = rows.find((x) => x.id === params.id);
                if (item == null) return null;

                // 同じグループのアイテムを抽出
                const groupItems = rows.filter(
                  (x) => x.groupId != null && x.groupId === item.groupId
                );

                // グループなし
                if (groupItems.length <= 1) {
                  return null;
                } else {
                  const isFirst = item.id === groupItems[0].id;
                  const isLast =
                    item.id === groupItems[groupItems.length - 1].id;
                  return (
                    <Box
                      borderTop={isFirst ? 4 : 0}
                      borderBottom={isLast ? 4 : 0}
                      borderRight={4}
                      width={16}
                      height={1}
                    />
                  );
                }
              },
            },
            ...(isInDeleteState || isGroupConfigMode
              ? [
                  {
                    ...GRID_CHECKBOX_SELECTION_COL_DEF,
                    renderHeader: () =>
                      isInDeleteState ? commonT('command.delete') : '選択',
                  },
                ]
              : [
                  {
                    field: ' ',
                    type: 'actions',
                    width: 50,
                    minWidth: 50,
                    renderCell: (params: GridRenderCellParams) => [
                      <KeyboardArrowRightIcon
                        key="move"
                        sx={{ color: (t) => t.palette.grey[500] }}
                      />,
                      <DragHandle
                        rowId={params.id.toString()}
                        key={params.id}
                      />,
                    ],
                  },
                ]),
          ]}
          sx={{
            '& .MuiDataGrid-virtualScrollerContent': {
              backgroundColor: 'transparent',
            },
          }}
          rows={displayRows}
          sortDisabled={isGroupConfigMode}
          onChange={onChange}
          getRowHeight={() => 'auto'}
          hideFooter
          disableColumnMenu
          disableMultipleColumnsSorting
          onRowClick={(params) => {
            if (isGroupConfigMode === false) {
              navigate(`${params.id}`);
            }
          }}
          rowSelectionModel={selection}
          checkboxSelection={isInDeleteState || isGroupConfigMode}
          onRowSelectionModelChange={(newSelection) => {
            const newIds = newSelection.map((x) => x.toString());
            if (isGroupConfigMode) {
              const getSameGroupItemIds = (id: string) => {
                const groupId = rows.find((x) => x.id === id)?.groupId;
                const sameGroupItemIds = rows
                  .filter((x) => groupId != null && x.groupId === groupId)
                  .map((x) => x.id);
                return sameGroupItemIds;
              };

              // 同一グループをまとめてON,OFFする
              const onId = Enumerable.from(newIds)
                .except(selection)
                .firstOrDefault();
              const offId = Enumerable.from(selection)
                .except(newIds)
                .firstOrDefault();
              if (onId != null) {
                const adjustedSelection = Enumerable.from(newIds)
                  .concat(getSameGroupItemIds(onId))
                  .distinct()
                  .toArray();
                setSelection(adjustedSelection);
              } else if (offId != null) {
                const adjustedSelection = Enumerable.from(newIds)
                  .except(getSameGroupItemIds(offId))
                  .toArray();
                setSelection(adjustedSelection);
              }
            } else {
              setSelection(newIds);
            }
          }}
          loading={
            categoriesResult.loading ||
            templateMessagesResult.loading ||
            removeTemplateMessagesResult.loading ||
            updateTemplateMessagesOrderResult.loading
          }
        />
      </AdminContent>
      {isInDeleteState && (
        <DeletionFooterMenu
          deleteButtonDisabled={selection.length === 0}
          deleteButtonText={t('template.deleteSelected')}
          onClickDelete={async () => {
            const result = await confirmDelete.open({
              title: t('template.deleteSelectedMsg'),
              message: t('template.unbackable'),
              okColor: 'primary',
            });
            if (result) {
              await removeTemplateMessages({
                variables: { input: { ids: selection } },
              });
              setSelection([]);
              setIsInDeleteState(false);
              templateMessagesResult.refetch();
              showSnackbar('success', t('template.deletedSelected'));
            }
          }}
          onClickCancel={() => setIsInDeleteState(false)}
        />
      )}
      {isGroupConfigMode && (
        <TemplateGroupFooterMenu
          onSave={async () => {
            // 二重送信ガード
            if (isSavingGroup) return;
            setIsSavingGroup(true);
            // 設定内容を保存する。失敗時はモードを閉じずエラー表示する
            // （中途半端に保存された印象を与えないため）
            try {
              await saveTemplateGroupSetting(rows);
              setIsGroupConfigMode(false);
              await templateMessagesResult.refetch();
            } catch (e) {
              showSnackbar('error', commonT('message.unexpectedError'));
            } finally {
              setIsSavingGroup(false);
            }
          }}
          onCancel={() => {
            setIsGroupConfigMode(false);
          }}
          canBind={canBind}
          onBind={() => {
            // 選択項目をグループにまとめる。
            // グループは使いまわさず、新規に作成してしまう。
            // 配置は1つ目の位置に合わせる。
            //
            // ・以下ようにリストを結合する。
            // 1つ目のインデックスより前の、バインド対象外のリスト
            //  +
            // バインド対象のリスト（グループIDは新規に設定）
            //  +
            // 1つ目のインデックスより後の、バインド対象外のリスト
            //  ↓
            // 新たなorderを割り当てる

            const orderdSelectedItems = Enumerable.from(selection)
              .select((id) => Enumerable.from(rows).first((x) => x.id === id))
              .orderBy((x) => x.order);
            const nobindRows = Enumerable.from(rows).where(
              (row) => selection.some((x) => x === row.id) === false
            );
            const topSelectionOrder = orderdSelectedItems.first().order;
            const nobindUpperRows = nobindRows.where(
              (x) => x.order < topSelectionOrder
            );
            const nobindLowerRows = nobindRows.where(
              (x) => x.order > topSelectionOrder
            );
            const newGroupId = uuidv4();
            const groupedRows = orderdSelectedItems.select((row) => ({
              ...row,
              groupId: newGroupId,
            }));
            // 結合してorderを振りなおす
            const newRows = nobindUpperRows
              .concat(groupedRows)
              .concat(nobindLowerRows)
              .select((row, i) => ({
                ...row,
                order: i + 1,
              }))
              .toArray();

            setRows(newRows);
            setSelection([]);

            // まだ実装していないが、サーバー上で保存処理はグループIDを指定したらそのグループIDのグループがなければ作成するようにする。
          }}
          canUnbind={canUnbind}
          onUnbind={() => {
            // グループから外す。
            // 同一グループはすべて選択されていて、単一の選択はありえないので、グループ情報を削除するだけでOK
            const newRows = Enumerable.from(rows)
              .select((row) => ({
                ...row,
                groupId: selection.some((x) => x === row.id)
                  ? undefined
                  : row.groupId,
              }))
              .toArray();

            setRows(newRows);
            setSelection([]);
          }}
        />
      )}
    </Box>
  );
};
export default TemplateIndex;
